/**
 * useClientOrgFilters
 *
 * Channel Partner / Advertiser options for the task filter bars, built from the same
 * partner→advertiser tree the create-task form's cascader uses, so every surface offers the
 * identical client set. PLATFORM only — lower levels have no partner layer above them, so
 * they fall back to the flat org list the caller already has.
 */

import { useCallback, useMemo } from 'react';
import { useClientOrgTree } from '../components/forms/clientOrgTree';

// Alphabetical, case-insensitive — same ordering the form's cascader uses
const byName = (a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());

const toOption = (node) => ({ id: node.value, name: node.label });

// A partner contributes its children; a childless root is itself a direct advertiser
const leavesOf = (nodes) =>
  nodes.reduce((acc, node) => acc.concat(node.isLeaf ? [node] : node.children || []), []);

const useClientOrgFilters = ({ level, userOrgId, selectedPartners = '', fallbackOrgs }) => {
  const isPlatform = level === 'PLATFORM';
  const { options: clientOrgTree, loading } = useClientOrgTree(
    isPlatform ? userOrgId : null,
    level
  );

  // Channel Partners = tree roots that have advertisers under them
  const channelPartnerOptions = useMemo(
    () => clientOrgTree.filter((node) => !node.isLeaf).map(toOption).sort(byName),
    [clientOrgTree]
  );

  // Advertisers narrow to the selected partners; with none selected it's every advertiser the
  // tree can reach — the same set the form's cascader lands on.
  const advertiserOptions = useMemo(() => {
    if (!isPlatform) return [...(fallbackOrgs || [])].sort(byName);
    const selected = selectedPartners ? selectedPartners.split(',').filter(Boolean) : [];
    const roots = selected.length
      ? clientOrgTree.filter((node) => selected.includes(node.value))
      : clientOrgTree;
    return leavesOf(roots).map(toOption).sort(byName);
  }, [isPlatform, selectedPartners, clientOrgTree, fallbackOrgs]);

  // Advertiser ids under the given partners, for dropping stale advertiser selections
  const advertisersUnder = useCallback(
    (partners) =>
      leavesOf(clientOrgTree.filter((node) => partners.includes(node.value))).map((n) => n.value),
    [clientOrgTree]
  );

  return {
    isPlatform,
    channelPartnerOptions,
    advertiserOptions,
    advertisersUnder,
    loading,
  };
};

export default useClientOrgFilters;
